/**
 * API Service Layer
 * Handles all communication with the backend API
 */

const API_BASE = '/api'

interface ApiResponse<T> {
  data?: T
  error?: string
}

// Get authentication token from localStorage
function getAuthToken(): string | null {
  return localStorage.getItem('pe_session_token')
}

async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<ApiResponse<T>> {
  try {
    // Build headers with auth token if available
    const token = getAuthToken()
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options?.headers as Record<string, string>),
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    })

    const data = await response.json()

    if (!response.ok) {
      return { error: data.error || `HTTP ${response.status}` }
    }

    return { data }
  } catch (error) {
    console.error('API Error:', error)
    return { error: error instanceof Error ? error.message : 'Network error' }
  }
}

// Health check
export async function checkHealth(): Promise<ApiResponse<{
  status: string
  version: string
  timestamp: string
}>> {
  return fetchApi('/health')
}

// Company search
export interface SearchMatch {
  ticker: string
  name: string
  exchange: string
  sector: string
  match_type: 'exact' | 'alias' | 'search' | 'local'
}

export interface SearchResult {
  query: string
  matches: SearchMatch[]
  total_count: number
  source?: string
}

export async function searchCompanies(query: string): Promise<ApiResponse<SearchResult>> {
  return fetchApi(`/search?q=${encodeURIComponent(query)}`)
}

// Company data
export interface CompanyInfo {
  ticker: string
  name: string
  sector: string
  industry: string
  marketCap: number
  employees: number
  founded: number
  headquarters: string
  description: string
}

export async function getCompany(ticker: string): Promise<ApiResponse<CompanyInfo>> {
  return fetchApi(`/company/${ticker}`)
}

export async function listCompanies(): Promise<ApiResponse<{
  companies: Array<{ ticker: string; name: string; sector: string }>
}>> {
  return fetchApi('/companies')
}

// Financial data
export interface FinancialData {
  ticker: string
  quoteType?: 'EQUITY' | 'ETF' | 'MUTUALFUND' | 'INDEX' | string
  revenue: number
  revenueGrowth: number
  grossProfit: number
  grossMargin: number
  operatingIncome: number
  operatingMargin: number
  netIncome: number
  netMargin: number
  eps: number
  peRatio: number
  freeCashFlow: number
  fcfMargin: number
  totalDebt: number
  totalCash: number
  netDebt: number
  debtToEquity: number
}

export async function getFinancials(ticker: string): Promise<ApiResponse<FinancialData>> {
  return fetchApi(`/financials/${ticker}`)
}

// FCF Predictions
export interface FCFPrediction {
  year: number
  fcf: number
  growth: number
  confidence: number
}

export interface PredictionsData {
  ticker: string
  quoteType?: 'EQUITY' | 'ETF' | 'MUTUALFUND' | 'INDEX' | string
  model?: string
  currentFcf: number
  predictions: FCFPrediction[]
  modelConfidence: number
  keyDrivers: string[]
  historicalFCF?: Array<{ year: number; fcf: number }>
  avgGrowthRate?: number
}

export async function getPredictions(ticker: string): Promise<ApiResponse<PredictionsData>> {
  return fetchApi(`/predictions/${ticker}`)
}

// Regime Classification
export interface RegimeData {
  ticker: string
  quoteType?: 'EQUITY' | 'ETF' | 'MUTUALFUND' | 'INDEX' | string
  currentRegime: 'growth' | 'stable' | 'transition' | 'distressed'
  regime?: string // Alias for currentRegime
  regimeConfidence: number
  regimeDescription?: string
  regimeProbabilities: {
    growth: number
    stable: number
    transition: number
    distressed: number
  }
  historicalRegimes: Array<{ year: number; regime: string }>
  riskFactors: string[]
}

export async function getRegime(ticker: string): Promise<ApiResponse<RegimeData>> {
  return fetchApi(`/regime/${ticker}`)
}

// Chat
export interface ChatContext {
  ticker?: string
  name?: string
  sector?: string
  industry?: string
  marketCap?: number
  revenue?: number
  fcf?: number
  regime?: string
}

export interface ChatResponse {
  response: string
  ticker?: string
  timestamp: string
  models_used?: string[]
  confidence?: number
  source?: string
}

export async function sendChatMessage(message: string, context?: ChatContext): Promise<ApiResponse<ChatResponse>> {
  return fetchApi('/chat', {
    method: 'POST',
    body: JSON.stringify({ message, context: context || {} }),
  })
}

// Analysis
export interface AnalysisJob {
  ticker: string
  status: string
  jobId: string
  message: string
  estimatedTime: string
}

export async function triggerAnalysis(ticker: string): Promise<ApiResponse<AnalysisJob>> {
  return fetchApi(`/analyze/${ticker}`, { method: 'POST' })
}

// Report Generation (async job-based)
export interface ReportJob {
  success: boolean
  job_id: string
  ticker: string
  status: 'queued' | 'running' | 'completed' | 'failed'
  message: string
  status_url: string
}

export interface ReportJobStatus {
  job_id: string
  ticker: string
  status: 'queued' | 'running' | 'completed' | 'failed'
  progress: number
  message: string
  report_id: string
  error: string
  created_at: string
  completed_at: string
}

export interface GeneratedReport {
  success: boolean
  ticker: string
  company_name: string
  report_id: string
  filename: string
  content: string
  generated_at: string
  download_url: string
  error?: string
}

export interface ReportListItem {
  report_id: string
  ticker: string
  type: string
  title: string
  content?: string
  sections: string[]
  created_at: string
  status: string
  // Legacy fields for backward compatibility (in case old reports exist)
  filename?: string
  download_url?: string
  size?: number
  modified?: number
}

// Backend response for generate-report - note: no 'success' wrapper
export interface ReportGenerationResponse {
  job_id: string
  ticker: string
  status: 'pending' | 'queued' | 'running' | 'completed' | 'failed'
  message: string
}

// Start report generation (returns job ID immediately)
// Note: Backend returns ReportGenerationResponse directly (no 'success' wrapper)
export async function generateReport(ticker: string): Promise<ApiResponse<ReportGenerationResponse>> {
  return fetchApi(`/generate-report/${ticker}`, { method: 'POST' })
}

// Poll for report generation status
export async function getReportStatus(jobId: string): Promise<ApiResponse<ReportJobStatus>> {
  return fetchApi(`/report-status/${jobId}`)
}

export async function listReports(): Promise<ApiResponse<{ reports: ReportListItem[] }>> {
  return fetchApi('/reports')
}

export async function downloadReport(reportId: string): Promise<string | null> {
  try {
    const response = await fetch(`${API_BASE}/reports/${reportId}/download`)
    if (!response.ok) {
      return null
    }
    return await response.text()
  } catch (error) {
    console.error('Report download error:', error)
    return null
  }
}

export interface DeleteReportResponse {
  success: boolean
  message: string
  deleted_file?: string
}

export async function deleteReport(reportId: string): Promise<ApiResponse<DeleteReportResponse>> {
  return fetchApi(`/reports/${reportId}`, { method: 'DELETE' })
}

// Utility functions
export function formatCurrency(value: number | undefined | null): string {
  if (value === undefined || value === null || isNaN(value)) return 'N/A'
  const absValue = Math.abs(value)
  const sign = value < 0 ? '-' : ''
  if (absValue >= 1e12) return `${sign}$${(absValue / 1e12).toFixed(2)}T`
  if (absValue >= 1e9) return `${sign}$${(absValue / 1e9).toFixed(2)}B`
  if (absValue >= 1e6) return `${sign}$${(absValue / 1e6).toFixed(2)}M`
  if (absValue >= 1e3) return `${sign}$${(absValue / 1e3).toFixed(2)}K`
  return `${sign}$${absValue.toFixed(2)}`
}

export function formatPercent(value: number | undefined | null): string {
  if (value === undefined || value === null || isNaN(value)) return 'N/A'
  return `${(value * 100).toFixed(1)}%`
}

export function formatNumber(value: number | undefined | null): string {
  if (value === undefined || value === null || isNaN(value)) return 'N/A'
  return new Intl.NumberFormat().format(value)
}
